import React, { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Edit2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AUTOFILL_OPT_OUT } from '@/lib/constants/autofill-opt-out';

interface SubjectFieldProps {
  subject: string;
  onSubjectChange: (subject: string) => void;
  placeholder?: string;
  className?: string;
}

export function SubjectField({
  subject,
  onSubjectChange,
  placeholder = 'Enter subject...',
  className
}: SubjectFieldProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(subject);
  const inputRef = useRef<HTMLInputElement>(null);

  const startEditing = () => {
    setEditValue(subject);
    setIsEditing(true);
  };

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue && trimmedValue !== subject) {
      onSubjectChange(trimmedValue);
    } else {
      setEditValue(subject); // Reset to original if empty
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(subject);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <div
        className={cn(
          'bg-surface-subtle/50 flex items-center gap-2 rounded px-3 py-2',
          className
        )}
      >
        <span className="text-muted-foreground text-sm font-medium">
          Subject:
        </span>
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          {...AUTOFILL_OPT_OUT}
          className="focus:ring-primary/20 focus:border-primary border-border-strong bg-card flex-1 rounded border px-2 py-1 text-sm focus:ring-2 focus:outline-hidden"
          placeholder={placeholder}
        />
        <div className="flex items-center gap-1">
          <Button
            size="icon-xs"
            variant="ghost"
            onClick={handleSave}
            title="Save"
          >
            <Check className="text-success-subtle-foreground h-4 w-4" />
          </Button>
          <Button
            size="icon-xs"
            variant="ghost"
            onClick={handleCancel}
            title="Cancel"
          >
            <X className="text-error-subtle-foreground h-4 w-4" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'group bg-surface-subtle/50 hover:bg-muted/50 flex cursor-text items-center gap-2 rounded px-3 py-2 transition-all',
        className
      )}
      onClick={startEditing}
      role="button"
      tabIndex={0}
      title="Click to edit subject"
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          startEditing();
        }
      }}
    >
      <div className="flex flex-1 items-center gap-2">
        <span className="text-muted-foreground text-sm font-medium">
          Subject:
        </span>
        <span
          className={cn(
            'text-sm',
            subject ? 'text-foreground' : 'text-muted-foreground italic'
          )}
        >
          {subject || placeholder}
        </span>
        <div className="text-muted-foreground flex items-center gap-1 text-xs">
          <Edit2 className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Click to edit</span>
        </div>
      </div>
    </div>
  );
}
